// GlobeView.js
import Globe from "react-globe.gl";
import * as THREE from "three";
import { useEffect, useMemo, useRef, useState } from "react";
import CitySearchBar from "./CitySearchBar";
import globeDestinations from "./globeDestinations";
import manualCities from "./manualCities";
import FiltersPanel from "./components/globe/FiltersPanel";
import DetailsPanel from "./components/globe/DetailsPanel";

// ---- Wikipedia helper (no API key needed)
const fetchWikipediaSummary = async (placeName) => {
  const trimmed = (placeName || "").trim();
  const base = trimmed.split(",")[0].trim();
  const country = (trimmed.split(",")[1] || "").trim();

  const candidates = [
    base, // "Sydney"
    trimmed, // "Sydney, Australia"
    `${base} (city)`,
    country ? `${base}, ${country}` : null,
  ].filter(Boolean);

  for (const c of candidates) {
    const title = encodeURIComponent(c.replace(/\s+/g, "_"));
    try {
      const res = await fetch(
        `https://en.wikipedia.org/api/rest_v1/page/summary/${title}`
      );
      if (!res.ok) continue;
      const data = await res.json();
      if (data?.extract && !/may refer to/i.test(data.extract)) {
        return data.extract;
      }
    } catch {}
  }
  return null;
};

const GlobeView = () => {
  const globeRef = useRef();

  const [countries, setCountries] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [highlightedCity, setHighlightedCity] = useState(null);
  const [cityInfo, setCityInfo] = useState(null);
  const [arcs, setArcs] = useState([]);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [filters, setFilters] = useState({ themes: [], budget: "" });

  // ---- Build base points
  const basePoints = useMemo(() => {
    const countryPoints = globeDestinations.map((d) => ({
      type: "country",
      name: d.title,
      lat: d.lat,
      lng: d.lng,
      summary: d.summary,
      popularity: d.popularity ?? 50,
    }));
    const cityPoints = manualCities.map((c) => ({
      type: "city",
      name: c.name,
      lat: c.lat,
      lng: c.lng,
      summary: c.summary,
      popularity: c.popularity ?? 60,
    }));
    return [...countryPoints, ...cityPoints];
  }, []);

  // ---- Filters (expanded themes)
  const filteredPoints = useMemo(() => {
    let pts = [...basePoints];
    if (filters.themes.length) {
      const themeMap = {
        beaches: /beach|island|coast|coastal|snorkel|scuba|sea|ocean/i,
        mountains: /mountain|himalaya|trek|peak|snow|alps|valley|hill/i,
        nightlife: /nightlife|party|bar|club|festival/i,
        culture: /culture|museum|art|heritage|festival|tradition/i,
        wildlife: /wildlife|safari|tiger|rhino|gorilla|national park|reserve/i,
        spiritual: /temple|monastery|spiritual|pilgrim|ashram|aarti|basilica/i,
        heritage: /unesco|heritage|fort|ruin|castle|ancient|archaeolog/i,
        adventure: /hike|trek|paraglid|ski|dive|surf|rafting|zip/i,
        food: /food|cuisine|street food|coffee|wine|restaurant|seafood/i,
      };
      pts = pts.filter((p) =>
        filters.themes.some((t) => themeMap[t]?.test(p.summary || ""))
      );
    }
    return pts;
  }, [basePoints, filters]);

  // ---- Load world polygons
  useEffect(() => {
    fetch("/world.geojson")
      .then((r) => r.json())
      .then((d) => setCountries(d.features || []));
  }, []);

  // ---- User location
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setUserLocation({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        label: "You",
        color: "dodgerblue",
        size: 0.28,
      });
    });
  }, []);

  // ---- Camera controls (HARD stop: no auto-rotate)
  useEffect(() => {
    if (!globeRef.current) return;

    const controls = globeRef.current.controls();
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0;
    controls.minDistance = 150;
    controls.maxDistance = 700;
    controls.update();

    // Safety loop for ~1s
    let frames = 0,
      raf;
    const stop = () => {
      frames++;
      const c = globeRef.current?.controls();
      if (c) {
        c.autoRotate = false;
        c.autoRotateSpeed = 0;
        c.update();
      }
      if (frames < 60) raf = requestAnimationFrame(stop);
    };
    stop();
    return () => cancelAnimationFrame(raf);
  }, []);

  // ---- Custom sprite pins (optional)
  const [pinTexture, setPinTexture] = useState(null);
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.load(
      "/assets/pin.png",
      (tex) => setPinTexture(tex),
      undefined,
      () => setPinTexture(null)
    );
  }, []);

  // ---- Render data for normal pins / objects
  const objectsData = useMemo(() => {
    if (!pinTexture) return [];
    return filteredPoints.map((p) => {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: pinTexture, depthWrite: false })
      );
      const scale = 8 + (p.popularity ?? 50) * 0.08;
      sprite.scale.set(scale, scale, 1);
      sprite.userData = { label: p.name };
      return { ...p, threeObj: sprite };
    });
  }, [filteredPoints, pinTexture]);

  const pointsData = useMemo(() => {
    if (pinTexture) return [];
    return filteredPoints.map((p) => ({
      ...p,
      color: p.type === "city" ? "#f59e0b" : "#38bdf8",
      size: 0.15 + (p.popularity ?? 50) * 0.004,
    }));
  }, [filteredPoints, pinTexture]);

  // ---- Selected BIG marker
  const selectedObjectsData = useMemo(() => {
    if (!pinTexture || !highlightedCity) return [];
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: pinTexture,
        depthWrite: false,
        color: 0xff4444,
      })
    );
    sprite.scale.set(18, 18, 1);
    return [{ ...highlightedCity, threeObj: sprite }];
  }, [highlightedCity, pinTexture]);

  const selectedPointsData = useMemo(() => {
    if (pinTexture || !highlightedCity) return [];
    return [{ ...highlightedCity, color: "#ef4444", size: 0.38 }];
  }, [highlightedCity, pinTexture]);

  // ---- Weather (Open-Meteo)
  const getWeatherText = async (lat, lng) => {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current_weather=true`;
      const res = await fetch(url);
      const data = await res.json();
      const cw = data?.current_weather;
      if (!cw) return null;
      return `${Math.round(cw.temperature)}°C, wind ${Math.round(
        cw.windspeed
      )} km/h`;
    } catch {
      return null;
    }
  };

  const bestTimeLookup = (name) => {
    const n = (name || "").toLowerCase();
    if (n.includes("goa")) return "Nov–Feb (dry & pleasant)";
    if (n.includes("manali") || n.includes("spiti")) return "Mar–Jun & Sep–Oct";
    if (n.includes("paris")) return "Apr–Jun & Sep–Oct";
    if (n.includes("sydney")) return "Sep–Nov & Mar–May";
    return null;
  };

  const flyTo = (lat, lng, altitude = 0.5) => {
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat, lng, altitude }, 1200);
    }
  };

  // const fetchTouristSummary = async (locationName) => {
  //   const cleaned = (locationName || '').split(',')[0].trim().toLowerCase();
  //   const manualMatch = manualCities.find(x => x.name.toLowerCase().includes(cleaned));
  //   if (manualMatch) return manualMatch.summary;
  //   const globeMatch = globeDestinations.find(x => x.title.toLowerCase().includes(cleaned));
  //   if (globeMatch) return globeMatch.summary;
  //   const wiki = await fetchWikipediaSummary(locationName);
  //   if (wiki) return wiki;
  //   return 'Tourist summary not available yet.';
  // };

  const selectLocation = async (loc) => {
    const name = loc.name || loc.label || loc.title;
    flyTo(loc.lat, loc.lng, 0.5);

    const [summary, weatherText] = await Promise.all([
      fetchTouristSummary(name),
      getWeatherText(loc.lat, loc.lng),
    ]);

    const info = {
      name,
      summary,
      lat: loc.lat,
      lng: loc.lng,
      bestTime: bestTimeLookup(name),
      weather: weatherText || undefined,
    };
    setCityInfo(info);
    setHighlightedCity({ ...loc, label: name });

    // draw arc from user to selected
    if (userLocation) {
      setArcs([
        {
          startLat: userLocation.lat,
          startLng: userLocation.lng,
          endLat: loc.lat,
          endLng: loc.lng,
          color: ["#60a5fa", "#f43f5e"],
        },
      ]);
      setTimeout(() => setArcs([]), 60000);
    }
  };

  const allArcs = useMemo(() => arcs, [arcs]);

  // heatmap weights
  const heatPoints = useMemo(
    () =>
      filteredPoints.map((p) => ({
        lat: p.lat,
        lng: p.lng,
        weight: (p.popularity ?? 50) / 100,
      })),
    [filteredPoints]
  );

  useEffect(() => {
    fetch("/world.geojson")
      .then((res) => res.json())
      .then((data) => setCountries(data.features));
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      setUserLocation({
        lat: latitude,
        lng: longitude,
        size: 0.3,
        color: "blue",
        label: "Your Location",
      });
    });
  }, []);

  // ✅ Updated function to clean and encode the Wikipedia title
  const fetchTouristSummary = async (locationName) => {
    const cleanedName = locationName.split(",")[0].trim().toLowerCase();

    // First try to find in manualCities (for city-specific entries)
    const manualMatch = manualCities.find((item) =>
      item.name.toLowerCase().includes(cleanedName)
    );
    if (manualMatch) return manualMatch.summary;

    // Then try to find in globeDestinations (for country-level entries)
    const globeMatch = globeDestinations.find((item) =>
      item.title.toLowerCase().includes(cleanedName)
    );
    if (globeMatch) return globeMatch.summary;

    // Default fallback
    // return "Tourist summary not available yet.";
    return null;
  };

  const handleCitySelect = async (location) => {
    if (globeRef.current) {
      globeRef.current.pointOfView(
        { lat: location.lat, lng: location.lng, altitude: 0.5 },
        1200
      );
    }

    const summary = await fetchTouristSummary(
      location.name || location.label || location.title
    );

    setCityInfo({
      name: location.name || location.label || location.title,
      summary: summary,
      lat: location.lat,
      lng: location.lng,
    });

    setHighlightedCity({
      ...location,
      size: 0.4,
      color: "red",
      label: location.name || "Selected City",
    });

    if (userLocation) {
      const newArc = {
        startLat: userLocation.lat,
        startLng: userLocation.lng,
        endLat: location.lat,
        endLng: location.lng,
        color: ["blue", "red"],
      };
      setArcs([newArc]);

      // Clear arcs after 60 seconds
      setTimeout(() => {
        setArcs([]);
      }, 60000);
    }
  };

  // const combinedPoints = [
  //   ...(userLocation ? [userLocation] : []),
  //   ...points,
  //   ...(highlightedCity ? [highlightedCity] : []),
  // ];

  return (
    <div
      style={{
        width: "100%",
        height: "700px",
        position: "relative",
        background: "#0b0b12",
      }}
    >
      {/* 🔹 Fixed Header for Search + Filter */}
      <div
        className="search-filter-bar"
        style={{
          position: "absolute",
          top: "15px",
          left: 0,
          width: "100%",
          zIndex: 50,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
          gap: "10px",
          flexWrap: "nowrap",
        }}
      >
        {/* Search Bar (Left) */}
        <div
          className="search-wrapper"
          style={{ flex: 1, maxWidth: "380px", position: "relative" }}
        >
          <CitySearchBar onSelectLocation={selectLocation} />
        </div>

        {/* Filters (Right) */}
        <div className="filter-wrapper" style={{ flexShrink: 0 }}>
          <FiltersPanel
            filters={filters}
            setFilters={setFilters}
            showHeatmap={showHeatmap}
            setShowHeatmap={setShowHeatmap}
          />
        </div>

        {/* ✅ Styles to lock layout & dropdown behavior */}
        <style>{`
    /* Desktop & mobile alignment */
    .search-filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: nowrap;
    }

    /* Search & filter alignment fix */
    .search-wrapper, .filter-wrapper {
      display: flex;
      align-items: center;
    }

    /* Keep dropdowns fixed (no overlap) */
    .search-results {
      position: fixed !important;
      top: 70px !important;
      left: 16px !important;
      width: 340px !important;
      max-height: 250px !important;
      z-index: 9999 !important;
    }

    .filter-dropdown {
      position: fixed !important;
      top: 70px !important;
      right: 16px !important;
      width: 300px !important;
      z-index: 9999 !important;
    }

    /* ✅ Mobile view */
    @media (max-width: 768px) {
      .search-filter-bar {
        flex-direction: row !important;
        justify-content: space-between !important;
        padding: 0 12px !important;
        width: 50%;
      }

      .search-wrapper {
        flex: 1;
        max-width: 50%;
      }

      .filter-wrapper {
        flex-shrink: 0;
      }

      .search-results {
        left: 12px !important;
        width: 50% !important;
        top: 70px !important;
      }

      .filter-dropdown {
        right: 12px !important;
        width: 260px !important;
        top: 70px !important;
      }
    }
  `}</style>
      </div>

      <DetailsPanel
        cityInfo={cityInfo}
        onClose={() => setCityInfo(null)}
        weather={cityInfo?.weather ? { text: cityInfo.weather } : null}
      />

      <Globe
        ref={globeRef}
        animateIn={false} // prevent initial spin
        // Textures
        globeImageUrl="https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
        bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
        backgroundImageUrl="https://unpkg.com/three-globe/example/img/night-sky.png"
        // Country polygons
        polygonsData={countries}
        polygonCapColor={() => "rgba(255,255,255,0.06)"}
        polygonSideColor={() => "rgba(255,255,255,0.04)"}
        polygonStrokeColor={() => "#3a3a48"}
        polygonAltitude={0.005}
        polygonLabel={({ properties }) =>
          properties.ADMIN || properties.name || "Unknown Country"
        }
        polygonHoverColor={() => "orange"}
        // Default points (plus selected when no custom sprites)
        pointsData={[...pointsData, ...selectedPointsData]}
        pointLat="lat"
        pointLng="lng"
        pointAltitude={() => 0.01}
        pointRadius={(p) => p.size || 0.2}
        pointColor="color"
        pointLabel={(p) =>
          `<div style="padding:4px 6px;"><b>${
            p.name
          }</b><br/><span style="opacity:.8">${p.summary || ""}</span></div>`
        }
        onPointClick={(p) => selectLocation(p)}
        // Custom sprite pins (plus selected sprite)
        objectsData={[...objectsData, ...selectedObjectsData]}
        objectLat="lat"
        objectLng="lng"
        objectAltitude={() => 0.01}
        objectThreeObject={(d) => d.threeObj}
        objectLabel={(d) =>
          `<div style="padding:4px 6px;"><b>${d.name}</b></div>`
        }
        onObjectClick={(obj) => selectLocation(obj)}
        // Optional label at the selected city
        labelsData={
          highlightedCity
            ? [
                {
                  lat: highlightedCity.lat,
                  lng: highlightedCity.lng,
                  text: cityInfo?.name || highlightedCity.name || "Selected",
                },
              ]
            : []
        }
        labelText="text"
        labelLat="lat"
        labelLng="lng"
        labelSize={1.2}
        labelColor={() => "#ffffff"}
        labelAltitude={0.02}
        // Arcs (user route + trending)
        arcsData={allArcs}
        arcColor={"color"}
        arcAltitude={0.2}
        arcStroke={1.2}
        arcDashLength={0.45}
        arcDashGap={1}
        arcDashInitialGap={2}
        arcDashAnimateTime={2800}
        // Heatmap (hex bins)
        hexBinPointsData={showHeatmap ? heatPoints : []}
        hexBinPointLat="lat"
        hexBinPointLng="lng"
        hexBinWeight="weight"
        hexAltitude={(d) => d.sumWeight * 0.6}
        hexTopColor={() => "rgba(99,102,241,0.9)"}
        hexSideColor={() => "rgba(99,102,241,0.4)"}
        hexBinResolution={3}
        // Atmosphere
        atmosphereColor="#60a5fa"
        atmosphereAltitude={0.15}
      />
    </div>
  );
};

export default GlobeView;
